/**
 * Public Verifier Surface V1
 * Read-only verification surface. No auth, no mutation, no execution.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Check, Shield, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import './PublicVerifierSurface.css';
import {
  generateShareableUrl,
  extractBundleFromUrl,
  extractSignatureFromUrl,
  ShareableBundlePayload,
  getOrGenerateKeyPair,
  signData,
  exportPublicKey,
  verifySignature,
  importPublicKey,
  generateKeyFingerprint,
  encodeShareableBundle,
  extractMultiSignatureFromUrl,
  MultiSignaturePayload,
  MultiSignatureEntry,
  VerifiedSignature,
  verifyMultiSignatures,
  calculateQuorumStatus,
  addSignatureToPayload,
  encodeMultiSignaturePayload
} from './shareableLinkUtils';

interface VerificationResult {
  success: boolean;
  valid: boolean;
  error?: string;
}

interface PdfEvidenceExportV1 {
  exportVersion: string;
  exportId: string;
  exportHash: string;
  exportedAt: string;
  evidenceCitations: any[];
  crossDocumentLinks: any[];
}

async function verifyPdfEvidenceExport(data: PdfEvidenceExportV1): Promise<VerificationResult> {
  if (!data.exportVersion || data.exportVersion !== 'xable_pdf_evidence_export/v1') {
    return { success: false, valid: false, error: 'Invalid export version' };
  }
  if (!data.exportId || !data.exportHash) {
    return { success: false, valid: false, error: 'Missing required fields' };
  }
  return { success: true, valid: true };
}

export function PublicVerifierSurface() {
  const [searchParams] = useSearchParams();
  const [inputMode, setInputMode] = useState<'upload' | 'paste'>('upload');
  const [jsonData, setJsonData] = useState<string>('');
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [bundleData, setBundleData] = useState<PdfEvidenceExportV1 | null>(null);
  const [error, setError] = useState<string>('');
  const [shareableUrl, setShareableUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [signatureStatus, setSignatureStatus] = useState<'valid' | 'invalid' | 'not-signed' | null>(null);
  const [signerIdentity, setSignerIdentity] = useState<string | null>(null);
  const [keyFingerprint, setKeyFingerprint] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState<boolean>(false);
  const [multiSignaturePayload, setMultiSignaturePayload] = useState<MultiSignaturePayload | null>(null);
  const [verifiedSignatures, setVerifiedSignatures] = useState<VerifiedSignature[]>([]);
  const [signaturesExpanded, setSignaturesExpanded] = useState<boolean>(false);

  // Auto-verify from shareable link on load
  useEffect(() => {
    const payload = extractBundleFromUrl(searchParams);
    if (payload && payload.bundle) {
      try {
        const jsonString = JSON.stringify(payload.bundle, null, 2);
        setJsonData(jsonString);
        setBundleData(payload.bundle);
        verifyPdfEvidenceExport(payload.bundle).then(verification => {
          setResult(verification);
        });

        // Check for single signature
        const sigData = extractSignatureFromUrl(searchParams);
        if (sigData) {
          verifySignatureOnLoad(sigData, payload);
        }

        // Check for multi-signature payload
        const multiSigPayload = extractMultiSignatureFromUrl(searchParams);
        if (multiSigPayload) {
          verifyMultiSignaturesOnLoad(multiSigPayload, payload.bundle);
        }
      } catch {
        setError('Invalid shareable link data.');
      }
    }
  }, [searchParams]);

  // Verify signature on load
  const verifySignatureOnLoad = async (sigData: any, payload: ShareableBundlePayload) => {
    try {
      const publicKey = await importPublicKey(sigData.publicKey);
      if (!publicKey) {
        setSignatureStatus('invalid');
        return;
      }

      const bundleJson = deterministicStringify(payload.bundle);
      const isValid = await verifySignature(publicKey, sigData.signature, bundleJson);
      setSignatureStatus(isValid ? 'valid' : 'invalid');
      setSignerIdentity(sigData.identity);

      const fp = await generateKeyFingerprint(publicKey);
      setKeyFingerprint(fp || 'Unknown');
    } catch {
      setSignatureStatus('invalid');
    }
  };

  // Verify multi-signatures on load
  const verifyMultiSignaturesOnLoad = async (multiSigPayload: MultiSignaturePayload, bundle: any) => {
    try {
      const verified = await verifyMultiSignatures(multiSigPayload, bundle);
      setMultiSignaturePayload(multiSigPayload);
      setVerifiedSignatures(verified);
    } catch {
      setError('Failed to verify multi-signatures.');
    }
  };

  // Deterministic stringify for signing
  const deterministicStringify = (obj: any): string => {
    if (obj === null || typeof obj !== 'object') {
      return JSON.stringify(obj);
    }
    if (Array.isArray(obj)) {
      return `[${obj.map(deterministicStringify).join(',')}]`;
    }
    const sortedKeys = Object.keys(obj).sort();
    const keyValuePairs = sortedKeys.map(key => {
      const value = obj[key];
      return `"${key}":${deterministicStringify(value)}`;
    });
    return `{${keyValuePairs.join(',')}}`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setJsonData(e.target?.result as string);
    reader.readAsText(file);
  };

  const handleVerify = async () => {
    setError('');
    setResult(null);
    setBundleData(null);
    setShareableUrl(null);
    if (!jsonData.trim()) {
      setError('Please provide JSON to verify.');
      return;
    }
    try {
      const parsed = JSON.parse(jsonData);
      setBundleData(parsed);
      const verification = await verifyPdfEvidenceExport(parsed);
      setResult(verification);
      
      // Generate shareable URL on successful verification
      if (verification.success) {
        const payload: ShareableBundlePayload = {
          bundle: parsed,
          bundleHash: parsed.exportHash || '',
          metadata: {
            timestamp: new Date().toISOString(),
            version: 'v1'
          }
        };
        const url = generateShareableUrl(payload);
        setShareableUrl(url);
      }
    } catch {
      setError('Invalid JSON format.');
    }
  };

  const handleCopyLink = async () => {
    if (!shareableUrl) return;
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link to clipboard.');
    }
  };

  const handleReset = () => {
    setJsonData('');
    setResult(null);
    setBundleData(null);
    setError('');
    setShareableUrl(null);
    setCopied(false);
    setSignatureStatus(null);
    setSignerIdentity(null);
    setKeyFingerprint(null);
    setMultiSignaturePayload(null);
    setVerifiedSignatures([]);
    setSignaturesExpanded(false);
  };

  const handleAddMySignature = async () => {
    if (!bundleData || !result?.success) return;

    setIsSigning(true);
    try {
      const keypair = await getOrGenerateKeyPair();
      if (!keypair) {
        setError('Failed to generate or load signing key.');
        setIsSigning(false);
        return;
      }

      const bundleJson = deterministicStringify(bundleData);
      const signature = await signData(keypair.privateKey, bundleJson);
      if (!signature) {
        setError('Failed to sign bundle.');
        setIsSigning(false);
        return;
      }

      const publicKeyBase64 = await exportPublicKey(keypair.publicKey);
      if (!publicKeyBase64) {
        setError('Failed to export public key.');
        setIsSigning(false);
        return;
      }

      const fingerprint = await generateKeyFingerprint(keypair.publicKey);
      if (!fingerprint) {
        setError('Failed to generate key fingerprint.');
        setIsSigning(false);
        return;
      }

      // Prompt for identity
      const identity = prompt('Enter your identity (email, org, or name):', 'user@example.com');
      if (!identity) {
        setIsSigning(false);
        return;
      }

      const newSignature: MultiSignatureEntry = {
        id: identity,
        publicKey: publicKeyBase64,
        signature,
        fingerprint
      };

      // Add to existing payload or create new one
      const currentPayload = multiSignaturePayload || { signatures: [] };
      const updatedPayload = addSignatureToPayload(currentPayload, newSignature);
      setMultiSignaturePayload(updatedPayload);

      // Verify all signatures
      const verified = await verifyMultiSignatures(updatedPayload, bundleData);
      setVerifiedSignatures(verified);
    } catch {
      setError('Failed to add signature.');
    } finally {
      setIsSigning(false);
    }
  };

  const handleCopyMultiSignedLink = async () => {
    if (!bundleData || !multiSignaturePayload) return;

    try {
      const sigsEncoded = encodeMultiSignaturePayload(multiSignaturePayload);
      if (!sigsEncoded) {
        setError('Failed to encode multi-signature payload.');
        return;
      }

      const baseUrl = window.location.origin + window.location.pathname;
      const bundleParam = encodeURIComponent(encodeShareableBundle({
        bundle: bundleData,
        bundleHash: bundleData.exportHash || ''
      }) || '');
      const multiSignedUrl = `${baseUrl}?bundle=${bundleParam}&sigs=${encodeURIComponent(sigsEncoded)}`;

      await navigator.clipboard.writeText(multiSignedUrl);
      setShareableUrl(multiSignedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy multi-signed link.');
    }
  };

  const handleSignAndCopy = async () => {
    if (!bundleData || !result?.success) return;

    setIsSigning(true);
    try {
      const keypair = await getOrGenerateKeyPair();
      if (!keypair) {
        setError('Failed to generate or load signing key.');
        setIsSigning(false);
        return;
      }

      const bundleJson = deterministicStringify(bundleData);
      const signature = await signData(keypair.privateKey, bundleJson);
      if (!signature) {
        setError('Failed to sign bundle.');
        setIsSigning(false);
        return;
      }

      const publicKeyBase64 = await exportPublicKey(keypair.publicKey);
      if (!publicKeyBase64) {
        setError('Failed to export public key.');
        setIsSigning(false);
        return;
      }

      // Prompt for identity
      const identity = prompt('Enter your identity (email, org, or name):', 'user@example.com');
      if (!identity) {
        setIsSigning(false);
        return;
      }

      // Construct signed URL
      const baseUrl = window.location.origin + window.location.pathname;
      const bundleParam = encodeURIComponent(encodeShareableBundle({
        bundle: bundleData,
        bundleHash: bundleData.exportHash || ''
      }) || '');
      const signedUrl = `${baseUrl}?bundle=${bundleParam}&sig=${encodeURIComponent(signature)}&pub=${encodeURIComponent(publicKeyBase64)}&id=${encodeURIComponent(identity)}`;

      await navigator.clipboard.writeText(signedUrl);
      setShareableUrl(signedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to sign and copy link.');
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <div className="public-verifier-surface">
      <div className="verifier-header">
        <h1>Public PDF Evidence Bundle Verifier</h1>
        <p>Verify bundles without console access. Read-only verification only.</p>
      </div>

      <div className="verifier-controls">
        <div className="input-mode-selector">
          <button
            className={`mode-button ${inputMode === 'upload' ? 'active' : ''}`}
            onClick={() => setInputMode('upload')}
          >
            Upload File
          </button>
          <button
            className={`mode-button ${inputMode === 'paste' ? 'active' : ''}`}
            onClick={() => setInputMode('paste')}
          >
            Paste JSON
          </button>
        </div>

        {inputMode === 'upload' && (
          <div className="upload-section">
            <input type="file" accept=".json" onChange={handleFileUpload} />
          </div>
        )}

        {inputMode === 'paste' && (
          <div className="paste-section">
            <textarea
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              placeholder="Paste PDF Evidence Bundle JSON..."
            />
          </div>
        )}

        <div className="action-buttons">
          <button onClick={handleVerify} disabled={!jsonData.trim()}>
            Verify
          </button>
          <button onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {result && bundleData && (
        <div className="verification-result">
          <div className={`verification-status ${result.valid ? 'pass' : 'fail'}`}>
            Status: {result.valid ? 'PASS' : 'FAIL'}
          </div>

          <div className="verification-details">
            <div className="detail-item">
              <span className="detail-label">Export ID:</span>
              <span className="detail-value">{bundleData.exportId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Export Hash:</span>
              <span className="detail-value code">{bundleData.exportHash}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Evidence Citations:</span>
              <span className="detail-value">{bundleData.evidenceCitations.length}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Cross-Document Links:</span>
              <span className="detail-value">{bundleData.crossDocumentLinks.length}</span>
            </div>
          </div>

          {shareableUrl && (
            <div className="shareable-link-section">
              <button
                onClick={handleCopyLink}
                className="copy-link-button"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Link Copied!' : 'Copy Shareable Link'}
              </button>
              <button
                onClick={handleSignAndCopy}
                disabled={isSigning}
                className="sign-link-button"
              >
                <Shield className="w-4 h-4" />
                {isSigning ? 'Signing...' : 'Sign & Copy Link'}
              </button>
            </div>
          )}

          {result?.success && (
            <div className="multi-signature-section">
              <div className="multi-signature-actions">
                <button
                  onClick={handleAddMySignature}
                  disabled={isSigning}
                  className="add-signature-button"
                >
                  <Plus className="w-4 h-4" />
                  {isSigning ? 'Adding...' : 'Add My Signature'}
                </button>
                {multiSignaturePayload && multiSignaturePayload.signatures.length > 0 && (
                  <button
                    onClick={handleCopyMultiSignedLink}
                    disabled={isSigning}
                    className="copy-multi-link-button"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy Multi-Signed Link'}
                  </button>
                )}
              </div>
            </div>
          )}

          {verifiedSignatures.length > 0 && (
            <div className="multi-signature-display">
              <div
                className="multi-signature-header"
                onClick={() => setSignaturesExpanded(!signaturesExpanded)}
              >
                <h3>Signatures</h3>
                <span className="signature-count">
                  {verifiedSignatures.length} signature{verifiedSignatures.length !== 1 ? 's' : ''} attached
                </span>
                {signaturesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>

              {signaturesExpanded && (
                <div className="multi-signature-list">
                  {multiSignaturePayload?.quorum && (
                    <div className="quorum-display">
                      <span className="quorum-label">Quorum:</span>
                      <span className="quorum-status">
                        {multiSignaturePayload.quorum.required} required —{' '}
                        {calculateQuorumStatus(verifiedSignatures, multiSignaturePayload.quorum.required) ? '✅ MET' : '❌ NOT MET'}
                      </span>
                    </div>
                  )}

                  {verifiedSignatures.map((verified, index) => (
                    <div key={index} className="signature-row">
                      <div className="signature-info">
                        <span className="detail-label">Signed by:</span>
                        <span className="detail-value">{verified.entry.id}</span>
                      </div>
                      <div className="signature-info">
                        <span className="detail-label">Fingerprint:</span>
                        <span className="detail-value code">{verified.entry.fingerprint}</span>
                      </div>
                      <div className={`signature-status-badge ${verified.status}`}>
                        {verified.status === 'valid' ? '✅ VALID' : verified.status === 'invalid' ? '❌ INVALID' : '⚠️ MALFORMED'}
                      </div>
                    </div>
                  ))}

                  <div className="multi-signature-disclaimer">
                    Signatures confirm this bundle was signed by these keys.
                    Identities are declared and not independently verified.
                    Signatures do not approve or execute anything.
                  </div>
                </div>
              )}
            </div>
          )}

          {signatureStatus && verifiedSignatures.length === 0 && (
            <div className="signature-section">
              <div className={`signature-status ${signatureStatus}`}>
                <Shield className="w-4 h-4 inline mr-2" />
                Signature: {signatureStatus === 'valid' ? '✅ VALID' : signatureStatus === 'invalid' ? '❌ INVALID' : '⚠️ NOT SIGNED'}
              </div>
              {signerIdentity && (
                <div className="signature-identity">
                  <span className="detail-label">Signed by:</span>
                  <span className="detail-value">{signerIdentity}</span>
                </div>
              )}
              {keyFingerprint && (
                <div className="signature-fingerprint">
                  <span className="detail-label">Key fingerprint:</span>
                  <span className="detail-value code">{keyFingerprint}</span>
                </div>
              )}
              <div className="signature-disclaimer">
                Signature confirms the bundle was signed by this key. Identity is not independently verified.
              </div>
            </div>
          )}

          {result.error && <div className="verification-error">{result.error}</div>}
        </div>
      )}
    </div>
  );
}
